/**
 * TVC (Television Commercial) Uploader Page
 * 
 * Premium themed upload form for TVC content.
 * Used at both MCG and Marvel venues.
 * 
 * Key difference from other uploaders:
 * - Files are prefixed with _TVC_ (e.g., STK_ABC123_TVC_filename.mp4)
 * - No pill selector needed (single placement)
 */

import React, { useState, useCallback, useRef } from "react";
import { useAuthUser } from "../../../hooks/useAuthUser";
import UserDetailsFields from "../../../components/uploaders/UserDetailsFields";
import { useVFContext } from "../../../context/VFContext";
import { useToast } from "../../../components/ui/UploaderToast";
import UploaderSection from "../../../components/uploaders/UploaderSection";
import UploadSuccessModal from "../../../components/modal/UploadSuccessModal";
import { TVC_SPEC, buildSpecLabel } from "../../../config/tvcSpecs";
import { generateSessionId, generateCrockfordTimeId } from "../../../services/idGenerator";

// ============================================================
// METADATA WEBHOOK
// ============================================================
//const METADATA_WEBHOOK = "https://hook.eu2.make.com/hs49hn7asl4ow6bx92kx1js28jpehkrt";
//const METADATA_WEBHOOK = "https://hook.eu2.make.com/dd68kvmmcbolqaejfitrrrvl5wiualvq";
const METADATA_WEBHOOK = "https://hook.eu2.make.com/fgninplw8emfjkontq8csdhjxxc2g00v";

// ============================================================
// STYLES - Premium Theme
// ============================================================
const styles = {
  page: {
    minHeight: "100vh",
    height: "100%",
    backgroundColor: "#f7f7f5",
    fontFamily: "'Space Grotesk', -apple-system, sans-serif",
  },
  header: {
    backgroundColor: "#111114",
    padding: "18px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#fff",
    margin: 0,
  },
  headerSub: {
    fontSize: 13,
    color: "rgba(255,255,255,0.5)",
    marginTop: 2,
  },
  venueBadge: {
    backgroundColor: "rgba(47, 214, 181, 0.15)",
    border: "1px solid rgba(47, 214, 181, 0.4)",
    color: "#2fd6b5",
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  accentLine: {
    height: 3,
    background: "linear-gradient(90deg, #2fd6b5, #84cc16)",
  },
  content: {
    padding: "32px 40px 48px",
    maxWidth: 1100,
    margin: "0 auto",
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 12,
  },
  uploadSection: {
    backgroundColor: "#fff",
    borderRadius: 14,
    border: "2px solid #2fd6b5",
    boxShadow: "0 0 0 4px rgba(47, 214, 181, 0.1), 0 4px 20px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    marginBottom: 28,
  },
  uploadHeader: {
    backgroundColor: "#111114",
    padding: "14px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  uploadTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  uploadTitleIcon: {
    color: "#2fd6b5",
  },
  uploadBody: {
    padding: 24,
  },
  specLabel: {
    marginBottom: 16,
    fontSize: 13,
    color: "#666",
    lineHeight: 1.5,
  },
  // Prepared files section (appears when files uploaded)
  preparedSection: {
    marginTop: 0,
    maxHeight: 0,
    opacity: 0,
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
  preparedSectionVisible: {
    marginTop: 20,
    maxHeight: 200,
    opacity: 1,
  },
  preparedBox: {
    backgroundColor: "#f0fdf9",
    border: "1px solid #2fd6b5",
    borderRadius: 10,
    padding: "16px 20px",
  },
  preparedHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  preparedTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  preparedTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111",
  },
  preparedCount: {
    backgroundColor: "#2fd6b5",
    color: "#000",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 10,
  },
  preparedHint: {
    fontSize: 12,
    color: "#666",
  },
  notesSection: {
    marginBottom: 32,
  },
  textarea: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 10,
    resize: "vertical",
    minHeight: 80,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  textareaFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
  charCount: {
    fontSize: 11,
    color: "#999",
    textAlign: "right",
    marginTop: 6,
  },
  footer: {
    paddingTop: 24,
    borderTop: "1px solid #e5e5e3",
    display: "flex",
    justifyContent: "flex-end",
  },
  submitBtn: {
    padding: "12px 32px",
    fontSize: 15,
    fontWeight: 600,
    color: "#000",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "all 0.15s ease",
  },
  submitBtnHover: {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(47, 214, 181, 0.35)",
  },
  submitBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 28,
  },
  field: {},
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 8,
    display: "block",
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  inputFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
};

// ============================================================
// CONSTANTS
// ============================================================
const MAX_DESC = 250;


// ============================================================
// TVC PAGE COMPONENT
// ============================================================
export function TVCPage({ venue }) {
  const context = useVFContext();
  const { email: authEmail, name: authName } = useAuthUser();
  const { showError, ToastComponent } = useToast();

  const activeVenue = venue || context.venue || "MCG";

  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [submissionTimeId, setSubmissionTimeId] = useState(() => generateCrockfordTimeId());
  const [name, setName] = useState("");
  const [email, setEmail] = useState(authEmail || "");
  const [description, setDescription] = useState("");
  const [partner, setPartner] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const [uploadResults, setUploadResults] = useState({ assets: [] });
  const activeUploads = useRef(0);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastUploadInfo, setLastUploadInfo] = useState(null);

  const handleUploadComplete = useCallback(({ file }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    setUploadResults((prev) => ({ assets: [...prev.assets, file] }));
  }, []);

  const handleUploadStart = useCallback(() => {
    activeUploads.current += 1;
  }, []);

  const handleUploadCancel = useCallback(({ fileId }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    setUploadResults((prev) => ({ assets: prev.assets.filter((a) => a.id !== fileId) }));
  }, []);

  const uploadCount = uploadResults.assets.length;

  const resetForm = useCallback(() => {
    activeUploads.current = 0;
    setUploadResults({ assets: [] });
    setSessionId(generateSessionId());
    setSubmissionTimeId(generateCrockfordTimeId());
    setDescription("");
    setPartner("");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeUploads.current > 0) {
      showError("Uploads are still in progress.", { duration: 12000 });
      return;
    }
    if (!partner.trim()) {
      showError("Please enter a Partner Name.", { duration: 9000 });
      return;
    }
    if (uploadCount === 0) {
      showError("Please upload at least one file.", { duration: 9000 });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        sessionId,
        submissionParent: context.submissionParent,
        completedAt: new Date().toISOString(),
        name: authName || name || context.submitterName,
        email: authEmail || email,
        partner: partner.trim(),
        description,
        venue: activeVenue,
        ContentUpload: {
          TVC: {
            ledType: "tvc",
            assets: uploadResults.assets,
            contentGroup: TVC_SPEC.contentGroup,
          },
        },
      };

      const res = await fetch(METADATA_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Submission failed (${res.status})`);
      
      // TVC uses unique IDs per file
      const clubCode = context.submitterShortName || "VF";
      const uploadedAssets = uploadResults.assets.map(asset => {
        const prefixedName = asset?.prefixedFileName || "";
        const parts = prefixedName.split("_");
        const contentId = parts.length >= 2 ? `${parts[0]}_${parts[1]}` : `${clubCode}_${submissionTimeId}`;
        return {
          contentId,
          fileName: asset?.originalFileName || "Uploaded file",
        };
      });

      // Auto-seed into commercial TVC reels (non-blocking)
      
      const destLabel = activeVenue === "Marvel" ? "Corner Screen - TVC" : "Big Screen - TVC";
      setLastUploadInfo({
        assets: uploadedAssets,
        destination: `${activeVenue} ${destLabel}`,
      });
      setShowSuccessModal(true);
      
      resetForm();
    } catch (err) {
      console.error("Submission error:", err);
      showError("Something went wrong. Please try again.", { duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = activeVenue === "Marvel" ? "Corner Screen TVC's & Highlights" : "Big Screen TVC's & Highlights";
  const subtitle = activeVenue === "Marvel"
    ? "Upload TVC content for Marvel Stadium corner screens"
    : "Upload TVC content for MCG big screen";
  const sectionTitle = activeVenue === "Marvel" ? "Corner Screen - TVC" : "Big Screen - TVC";

  return (
    <div style={styles.page}>
      {/* Dark Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>{title}</h1>
          <p style={styles.headerSub}>{subtitle}</p>
        </div>
        <span style={styles.venueBadge}>{activeVenue === "Marvel" ? "Marvel Stadium" : "MCG"}</span>
      </header>
      
      {/* Teal accent line */}
      <div style={styles.accentLine} />

      {/* Content */}
      <main style={styles.content}>
        <form onSubmit={handleSubmit}>
          
          {/* User Details */}
          <UserDetailsFields
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            styles={styles}
          />

          {/* Partner Name */}
          <div style={{ marginBottom: 28 }}>
            <label style={styles.label}>
              Partner Name <span style={{ color: "#e53e3e" }}>*</span>
            </label>
            <input
              type="text"
              value={partner}
              onChange={(e) => setPartner(e.target.value)}
              onFocus={() => setFocusedField("partner")}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter partner name…"
              style={{
                ...styles.input,
                maxWidth: 400,
                ...(focusedField === "partner" ? styles.inputFocus : {}),
              }}
            />
          </div>

          {/* Upload Section */}
          <div style={styles.uploadSection}>
            <div style={styles.uploadHeader}>
              <h2 style={styles.uploadTitle}>
                <svg style={styles.uploadTitleIcon} width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3v9M5 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 13v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {sectionTitle}
              </h2>
              {uploadCount > 0 && (
                <span style={{
                  backgroundColor: "#2fd6b5",
                  color: "#000",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 10,
                }}>
                  {uploadCount} file{uploadCount !== 1 ? "s" : ""} ready
                </span>
              )}
            </div>
            
            <div style={styles.uploadBody}>
              <div style={styles.specLabel}>{buildSpecLabel(TVC_SPEC)}</div>
              <UploaderSection
                key={sessionId}
                uploadId="TVC"
                spec={TVC_SPEC}
                context={context}
                sessionId={sessionId}
                submissionTimeId={submissionTimeId}
                onUploadComplete={handleUploadComplete}
                onUploadStart={handleUploadStart}
                onUploadCancel={handleUploadCancel}
                filenamePrefix="TVC"
                hideHeader
              />
              
              {/* Prepared Files - transitions in when files uploaded */}
              <div style={{
                ...styles.preparedSection,
                ...(uploadCount > 0 ? styles.preparedSectionVisible : {}),
              }}>
                <div style={styles.preparedBox}>
                  <div style={styles.preparedHeader}>
                    <div style={styles.preparedTitleRow}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: "#2fd6b5" }}>
                        <path d="M3 8l4 4 6-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span style={styles.preparedTitle}>Prepared Files</span>
                      <span style={styles.preparedCount}>{uploadCount}</span>
                    </div>
                    <span style={styles.preparedHint}>Ready for submission</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div style={styles.notesSection}>
            <div style={styles.sectionLabel}>Notes (Optional)</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))}
              onFocus={() => setFocusedField("notes")}
              onBlur={() => setFocusedField(null)}
              placeholder="Add any notes for the operations team..."
              style={{
                ...styles.textarea,
                ...(focusedField === "notes" ? styles.textareaFocus : {}),
              }}
            />
            <div style={styles.charCount}>{description.length}/{MAX_DESC}</div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="submit"
              disabled={isSubmitting || uploadCount === 0 || !partner.trim()}
              onMouseEnter={() => setIsSubmitHovered(true)}
              onMouseLeave={() => setIsSubmitHovered(false)}
              style={{
                ...styles.submitBtn,
                ...(isSubmitHovered && !isSubmitting && uploadCount > 0 && partner.trim() ? styles.submitBtnHover : {}),
                ...((isSubmitting || uploadCount === 0 || !partner.trim()) ? styles.submitBtnDisabled : {}),
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </form>
      </main>

      {ToastComponent}
      
      {/* Upload Success Modal */}
      <UploadSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        assets={lastUploadInfo?.assets || []}
        destination={lastUploadInfo?.destination}
      />
    </div>
  );
}

// ============================================================
// VENUE-SPECIFIC EXPORTS
// ============================================================
export function MCGTVCPage() {
  return <TVCPage venue="MCG" />;
}

export function MarvelTVCPage() {
  return <TVCPage venue="Marvel" />;
}

export default TVCPage;
